import axios from 'axios';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import * as Yup from 'yup';
import { useClients } from '../../hooks/useClients';
import { IAuth0User } from '../../interfaces/IAuth0User';
import Http from '../../services/http';
import FormModal from '../FormModal';
import { useNotifications } from '../notifications/useNotifications';
import { ClientCodeMultiselect } from './ClientCodeMultiselect';

interface Props {
  user: IAuth0User;
  open: boolean;
  onClose: () => void;
}

interface IUpdateClientCode {
  client_code: string[];
}

const schema = Yup.object().shape({
  client_code: Yup.array().min(1, 'Required'),
});

const normalizeToArray = (value?: string[] | string): string[] => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  return value ? [value] : [];
};

const EditUserModal = ({ user, open, onClose }: Props) => {
  const { data: clients } = useClients();
  const { pushNotification } = useNotifications();
  const queryClient = useQueryClient();

  const mutation = useMutation<unknown, unknown, IUpdateClientCode>(
    ({ client_code }) => {
      return axios.patch(
        `${process.env.REACT_APP_API}/user/${user.user_id}/client-code`,
        { client_code },
        {
          headers: {
            Authorization: `Bearer ${Http.accessToken}`,
          },
        }
      );
    },
    {
      onSuccess: (_, { client_code }) => {
        queryClient.setQueryData('users', (oldData: any) => {
          if (!oldData) return oldData;

          return oldData.map((u: IAuth0User) => {
            if (u.user_id === user.user_id) {
              return {
                ...u,
                user_metadata: {
                  ...u.user_metadata,
                  client_code,
                },
              };
            }
            return u;
          });
        });

        pushNotification({
          heading: 'Client Code Updated',
          content: `Client code assignment updated for ${
            user.nickname || user.name || user.email
          }.`,
        });

        onClose();
      },
    }
  );

  return (
    <FormModal open={open} reportOpen={onClose}>
      <Formik
        initialValues={{
          client_code: normalizeToArray(user.user_metadata?.client_code),
        }}
        onSubmit={(values) => {
          mutation.mutate({
            client_code: values.client_code.filter(Boolean) as string[],
          });
        }}
        validateOnMount
        validationSchema={schema}
      >
        {({ isSubmitting, isValid }) => (
          <Form>
            <fieldset disabled={isSubmitting}>
              <h3 className="font-medium mb-4 text-gray-900">
                Edit Client Code
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {user.nickname || user.name || user.email}
              </p>

              <ClientCodeMultiselect
                clients={clients}
                disabled={!clients?.length}
              />

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  className={classNames(
                    'inline-flex justify-center py-2 px-4 focus:outline-none focus:ring-2 focus:ring-offset-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white',
                    {
                      'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500':
                        isValid && !isSubmitting,
                      'bg-indigo-300 hover:bg-indigo-300 focus:ring-indigo-300':
                        !isValid || isSubmitting,
                    }
                  )}
                >
                  Save
                </button>
              </div>
            </fieldset>
          </Form>
        )}
      </Formik>
    </FormModal>
  );
};

export default EditUserModal;
